/**
 * User Validation Schemas (Yup)
 * Defines validation rules for all user-related forms
 *
 * @module Features/UserManagement/Validation
 */

import * as yup from 'yup';
import { CreateUserRequest, UpdateUserRequest } from '../Types';

/**
 * Email validation rule
 * - Required
 * - Valid email format
 * - Max 255 chars
 */
const emailSchema = yup
  .string()
  .required('Email is required')
  .email('Invalid email format')
  .max(255, 'Email must be 255 characters or less')
  .lowercase('Email must be lowercase');

/**
 * First name validation rule
 * - Required
 * - Min 2 chars
 * - Max 100 chars
 * - Polish characters allowed
 */
const firstNameSchema = yup
  .string()
  .required('First name is required')
  .min(2, 'First name must be at least 2 characters')
  .max(100, 'First name must be 100 characters or less')
  .matches(
    /^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s-]*$/,
    'First name can only contain letters, spaces, and hyphens'
  );

/**
 * Last name validation rule
 * - Required
 * - Min 2 chars
 * - Max 100 chars
 * - Polish characters allowed
 */
const lastNameSchema = yup
  .string()
  .required('Last name is required')
  .min(2, 'Last name must be at least 2 characters')
  .max(100, 'Last name must be 100 characters or less')
  .matches(
    /^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s-]*$/,
    'Last name can only contain letters, spaces, and hyphens'
  );

/**
 * Roles validation rule
 * - Required
 * - At least one role
 * - Valid roles (Admin, Clerk)
 */
const rolesSchema = yup
  .array()
  .of(yup.string().oneOf(['Admin', 'Clerk'], 'Invalid role'))
  .min(1, 'At least one role must be selected')
  .required('Roles are required');

/**
 * Password validation rule (for optional password in creation)
 * - Optional
 * - If provided: min 8 chars
 * - Must contain uppercase, lowercase, number, special char
 */
const passwordSchema = yup
  .string()
  .optional()
  .min(8, 'Password must be at least 8 characters')
  .test(
    'password-strength',
    'Password must contain uppercase, lowercase, number, and special character',
    (value) => {
      if (!value) return true; // optional
      return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(value);
    }
  );

/**
 * Validation schema for creating a new user
 * Includes email verification toggle
 */
export const createUserValidationSchema = yup.object().shape({
  email: emailSchema,
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  roles: rolesSchema,
  initialPassword: passwordSchema,
  sendWelcomeEmail: yup.boolean().required('Must specify if welcome email should be sent'),
  requireEmailVerification: yup.boolean().required('Must specify email verification requirement'),
});

/**
 * Validation schema for updating an existing user
 */
export const updateUserValidationSchema = yup.object().shape({
  email: emailSchema,
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  roles: rolesSchema,
  isActive: yup.boolean().required('Active status is required'),
  mustChangePassword: yup.boolean().required('Must specify password change requirement'),
  unlockAccount: yup.boolean().required('Must specify account lock status'),
});

/**
 * Type inference for form data
 */
export type CreateUserFormData = yup.InferType<typeof createUserValidationSchema>;
export type UpdateUserFormData = yup.InferType<typeof updateUserValidationSchema>;

/**
 * Helper function to validate email uniqueness (async)
 * This would typically call the API
 */
export const validateEmailUniqueness = async (
  email: string,
  excludeUserId?: number
): Promise<boolean> => {
  try {
    // TODO: Implement API call to check email uniqueness
    // For now, just return true
    return Promise.resolve(true);
  } catch {
    return Promise.resolve(false);
  }
};

/**
 * Helper function to sanitize form data before submission
 */
export const sanitizeCreateUserData = (data: CreateUserFormData): CreateUserRequest => ({
  ...data,
  email: data.email.toLowerCase().trim(),
  firstName: data.firstName.trim(),
  lastName: data.lastName.trim(),
  roles: data.roles.filter((role): role is string => role !== undefined),
});

/**
 * Helper function to sanitize update data
 */
export const sanitizeUpdateUserData = (data: UpdateUserFormData): UpdateUserRequest => ({
  ...data,
  email: data.email.toLowerCase().trim(),
  firstName: data.firstName.trim(),
  lastName: data.lastName.trim(),
  roles: data.roles.filter((role): role is string => role !== undefined),
});
